import { XCircle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { CloseButton } from '@/lib/base/buttons/close-button';
import { useViewHealthMarker } from '@/lib/view-health';

import { ConnectStripeButton } from './components/ConnectStripeButton';
import { StripeAccountCard } from './components/StripeAccountCard';
import { StripeBackfillWizard } from './components/StripeBackfillWizard';
import { useStripeConnections } from './hooks/useStripeConnections';

// Keyed by both authorize-failure reasons (set by ConnectStripeButton's onError)
// and ?stripe_error= redirect values from the callback. Only authorize_failed
// and oauth_failed invite a retry — every other class is non-retryable, so the
// copy says what happened and points at the right next step instead.
const STRIPE_ERROR_MESSAGES: Record<string, string> = {
  not_configured:
    'Stripe isn’t set up for this Ahoy environment yet. Reach out to your account admin to enable it.',
  not_permitted:
    'Only account admins can connect Stripe. Ask an admin to set up the connection.',
  authorize_failed:
    'We couldn’t reach Stripe to start the connection. Check your internet connection and try again.',
  oauth_failed:
    'The Stripe connection was cancelled before it finished. Connect again when you’re ready.',
  user_mismatch:
    'That Stripe connection didn’t match your Ahoy session. Sign in again, then reconnect Stripe.',
  admin_required:
    'Stripe needs to be connected by an account admin. Ask an admin to finish setting it up.',
  integration_disabled:
    'Stripe isn’t enabled for this workspace. Ask an account admin to enable it.',
  signature_verification_failed:
    'We couldn’t verify Stripe’s response, so the account wasn’t connected. Reach out to your account admin if this keeps happening.',
  already_connected:
    'This Stripe account is already connected to Ahoy. Check the connection below, or refresh the page if you don’t see it.',
  connect_race:
    'This Stripe account is already being connected. Refresh to see its current status.',
  internal_error:
    'Something went wrong on our end while connecting Stripe. Reach out to your account admin if this keeps happening.',
};

const DEFAULT_STRIPE_ERROR_MESSAGE =
  'Something went wrong while connecting Stripe. If this keeps happening, reach out to your account admin.';

function StripeUnavailableNotice() {
  return (
    <div className='rounded-lg border border-utility-warning-200 bg-utility-warning-50 p-4'>
      <p className='text-sm font-semibold text-utility-warning-900'>
        Stripe isn’t available in this environment
      </p>
      <p className='mt-1 text-sm text-utility-warning-700'>
        Stripe hasn’t been set up for this Ahoy environment yet, so it can’t be
        connected here.
      </p>
    </div>
  );
}

export function StripeSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const isAdmin = usePermissions().hasPermissionGroup('ahoy_other_admin');

  const [searchParams, setSearchParams] = useSearchParams();
  useViewHealthMarker('settings-section-ready', { ready: true });

  const { data, isLoading, isFetching, isError } = useStripeConnections(orgId);
  const connections = useMemo(() => data?.connections ?? [], [data]);
  const stripeUnavailable = data?.configured === false;

  const [oauthError, setOauthError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardConnectionId, setWizardConnectionId] = useState<string | null>(
    null
  );
  const [pendingBackfill, setPendingBackfill] = useState(false);

  // The backend OAuth success redirect lands here with ?stripe_oauth=success
  // (it does not carry a connection id). Capture that flag once and strip it
  // from the URL; the actual connection is resolved from the loaded list below.
  useEffect(() => {
    const oauth = searchParams.get('stripe_oauth');
    const error = searchParams.get('stripe_error');
    setOauthError(error);
    if (oauth === 'success') {
      setPendingBackfill(true);
      setSearchParams(
        prev => {
          const nextParams = new URLSearchParams(prev);
          nextParams.delete('stripe_oauth');
          return nextParams;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  // Once the freshly connected account has loaded, open the backfill wizard for
  // it. Since the redirect carries no connection id, target the most recently
  // connected account. Wait for a settled, successful fetch (!isFetching &&
  // !isError) before acting: a still-in-flight refetch may briefly expose the
  // cached pre-connect connection list, and opening the wizard on that would
  // target a stale account instead of the one just connected.
  useEffect(() => {
    if (!pendingBackfill || isFetching || isError || connections.length === 0) {
      return;
    }
    const newest = connections.reduce((latest, c) =>
      (c.connected_at ?? '') > (latest.connected_at ?? '') ? c : latest
    );
    setWizardConnectionId(newest.connection_id);
    setWizardOpen(true);
    setPendingBackfill(false);
  }, [pendingBackfill, isFetching, isError, connections]);

  const dismissError = useCallback(() => {
    setOauthError(null);
    setSearchParams(
      prev => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('stripe_error');
        return nextParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const handleConnectError = useCallback((reason: string) => {
    setOauthError(reason);
  }, []);

  const handleWizardOpenChange = useCallback((open: boolean) => {
    setWizardOpen(open);
  }, []);

  const handleFinished = useCallback(() => {
    setWizardOpen(false);
  }, []);

  return (
    <div className='space-y-8'>
      <header>
        <h2 className='text-lg font-semibold text-fg-primary'>Stripe</h2>
        <p className='mt-0.5 text-sm text-fg-tertiary'>
          Sync your Stripe customers, subscriptions, and payments into Ahoy.
        </p>
      </header>

      <div className='space-y-6'>
        {oauthError && (
          <div
            role='alert'
            className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'
          >
            <div className='flex items-start gap-3'>
              <XCircle className='mt-0.5 h-5 w-5 shrink-0 text-utility-error-600' />
              <div className='flex-1'>
                <p className='text-sm font-semibold text-utility-error-900'>
                  We couldn't connect Stripe
                </p>
                <p className='mt-1 text-sm text-utility-error-700'>
                  {STRIPE_ERROR_MESSAGES[oauthError] ??
                    DEFAULT_STRIPE_ERROR_MESSAGE}
                </p>
              </div>
              <CloseButton
                onPress={dismissError}
                label='Dismiss'
                size='sm'
                className='-my-1.5 -mr-1.5 shrink-0'
              />
            </div>
          </div>
        )}

        {/* Only show the error banner when there is nothing cached to fall back on. The
            connections query retains its last successful result across a transient refetch
            failure, so if we still have connections we render them (below) instead of
            blanking the section behind an error. */}
        {isError && connections.length === 0 && (
          <div
            role='alert'
            className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'
          >
            <p className='text-sm text-utility-error-700'>
              Couldn&apos;t load your Stripe connections. Please refresh and try
              again.
            </p>
          </div>
        )}

        {isLoading && connections.length === 0 && (
          <div
            role='status'
            className='text-sm text-[var(--color-text-secondary)]'
          >
            Loading…
          </div>
        )}

        {!isLoading && connections.length > 0 && orgId !== undefined && (
          <div className='space-y-4'>
            {connections.map(c => (
              <StripeAccountCard
                key={c.connection_id}
                orgId={orgId}
                connection={c}
                canManage={isAdmin}
                stripeUnavailable={stripeUnavailable}
                onReconnectError={handleConnectError}
              />
            ))}
            {isAdmin && !stripeUnavailable && (
              <ConnectStripeButton
                orgId={orgId}
                label='Connect another account'
                variant='secondary'
                onError={handleConnectError}
              />
            )}
          </div>
        )}

        {!isError &&
          !isLoading &&
          connections.length === 0 &&
          stripeUnavailable && <StripeUnavailableNotice />}

        {!isError &&
          !isLoading &&
          connections.length === 0 &&
          isAdmin &&
          orgId !== undefined &&
          !stripeUnavailable && (
            <div className='max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6'>
              {/* Stripe App permission-list copy will go here once the companion Stripe App setup issue lands. */}
              <ConnectStripeButton
                orgId={orgId}
                label='Connect Stripe account'
                variant='primary'
                onError={handleConnectError}
              />
            </div>
          )}

        {!isError &&
          !isLoading &&
          connections.length === 0 &&
          !isAdmin &&
          !stripeUnavailable && (
            <div className='rounded-lg border border-utility-warning-200 bg-utility-warning-50 p-4'>
              <p className='text-sm text-utility-warning-700'>
                Stripe can only be connected by an account admin.
              </p>
            </div>
          )}

        {isAdmin && orgId !== undefined && (
          <StripeBackfillWizard
            orgId={orgId}
            connectionId={wizardConnectionId}
            isOpen={wizardOpen}
            onOpenChange={handleWizardOpenChange}
            onFinished={handleFinished}
          />
        )}
      </div>
    </div>
  );
}
